// File: components/HireConfigPeriod.jsx
import React from 'react';

const HireConfigPeriod = ({
  period,
  updatePeriod,
  removePeriod,
  isLastPeriod,
  isFirstPeriod,
  configOptions,
  maxPeriods,
  hideEmploymentType = false,
  hireConfigPeriodInfo
}) => {
  // Helper functions to handle changes
  const handleHoursChange = (e) => {
    const newHours = Number(e.target.value);
    const updatedPeriod = { ...period, hoursPerWeek: newHours };
    updatePeriod(updatedPeriod);

    // If this is the first period, also update the salary to scale with hours
    if (isFirstPeriod && period.id.includes('default')) {
      // Signal that the hours have changed on the primary period
      // This will be handled in HireMultiPeriodConfig and Hires.jsx
      updatePeriod(updatedPeriod, true);
    }
  };

  const handleEmploymentChange = (newType) => {
    updatePeriod({ ...period, employmentType: newType });
  };

  const handleStartMonthChange = (e) => {
    const newMonth = Number(e.target.value);
    updatePeriod({ ...period, startMonth: newMonth });
  };

  const handleEndMonthChange = (e) => {
    const newMonth = Number(e.target.value);
    updatePeriod({ ...period, endMonth: newMonth });
  };

  // Employment type options
  const employmentTypes = [
    { value: 'employee', label: configOptions.employeeLabel },
    { value: 'contractor', label: configOptions.contractorLabel }
  ];

  // Create month options for the dropdown
  const startMonthOptions = [];
  for (let i = 0; i <= 12; i++) {
    startMonthOptions.push({
      value: i,
      label: i === 0
        ? (configOptions?.immediateHireLabel || hireConfigPeriodInfo?.immediateHireLabel || "Start (Month 0)")
        : `${configOptions?.monthLabel || hireConfigPeriodInfo?.monthLabel || "Month"} ${i}`
    });
  }

  // End month options should be after start month
  const endMonthOptions = [];
  // Minimum end month is one month after start
  const minEndMonth = Math.max(period.startMonth + 1, 1);
  for (let i = minEndMonth; i <= 12; i++) {
    endMonthOptions.push({
      value: i,
      label: i === 12
        ? (configOptions?.yearEndLabel || hireConfigPeriodInfo?.yearEndLabel || "End of Year")
        : `${configOptions?.monthLabel || hireConfigPeriodInfo?.monthLabel || "Month"} ${i}`
    });
  }

  // Calculate months duration
  const periodDuration = period.endMonth - period.startMonth;

  return (
    <div className="p-3 border rounded-md mb-3 bg-white">
      <div className="flex justify-between items-center mb-2">
        <h5 className="font-medium">
          {period.startMonth === 0 && period.endMonth === 12
            ? (configOptions?.fullYearLabel || hireConfigPeriodInfo?.fullYearLabel || "Full Year")
            : `${configOptions?.monthsLabel || hireConfigPeriodInfo?.monthsLabel || "Months"} ${period.startMonth} - ${period.endMonth}`}
          {periodDuration === 1
            ? ` (${periodDuration} ${configOptions?.monthLabel || hireConfigPeriodInfo?.monthLabel || "Month"})`
            : ` (${periodDuration} ${configOptions?.monthsLabel || hireConfigPeriodInfo?.monthsLabel || "Months"})`}
        </h5>
        {!isLastPeriod && (
          <div className="text-xs text-gray-500">
            {configOptions?.followedByLabel || hireConfigPeriodInfo?.followedByLabel || "Followed by next period →"}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Left column */}
        <div>
          {/* Start Month */}
          <div className="mb-3">
            <label className="block text-sm mb-1">{configOptions?.periodStartLabel || hireConfigPeriodInfo?.startLabel || "Start"}:</label>
            {isFirstPeriod ? (
              <select
                value={period.startMonth}
                onChange={handleStartMonthChange}
                className="w-full p-2 border rounded"
              >
                {startMonthOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <div className="w-full p-2 border rounded bg-gray-100 text-gray-700">
                {period.startMonth === 0
                  ? (configOptions?.immediateHireLabel || hireConfigPeriodInfo?.immediateHireLabel || "Start (Month 0)")
                  : `${configOptions?.monthLabel || hireConfigPeriodInfo?.monthLabel || "Month"} ${period.startMonth}`}
                <span className="text-xs text-gray-500 ml-2">{hireConfigPeriodInfo?.automaticallySetText || "(automatically set)"}</span>
              </div>
            )}
          </div>

          {/* Hours Per Week */}
          <div className="mb-3">
            <label className="block text-sm mb-1">
              {configOptions?.hoursPerWeekLabel || hireConfigPeriodInfo?.hoursPerWeekLabel || "Hours per week"}: <span className="font-medium">{period.hoursPerWeek}</span>
              {period.hoursPerWeek < parseInt(hireConfigPeriodInfo?.hoursRangeMax || "40") &&
                <span className="text-orange-500 ml-1">({Math.round(period.hoursPerWeek/parseInt(hireConfigPeriodInfo?.hoursRangeMax || "40")*100)}%)</span>}
            </label>
            <input
              type="range"
              min={hireConfigPeriodInfo?.hoursRangeMin || "5"}
              max={hireConfigPeriodInfo?.hoursRangeMax || "40"}
              step="5"
              value={period.hoursPerWeek}
              onChange={handleHoursChange}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{hireConfigPeriodInfo?.hoursRangeMin || "5"}</span>
              <span>{hireConfigPeriodInfo?.hoursRangeMax || "40"}</span>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div>
          {/* End Month */}
          <div className="mb-3">
            <label className="block text-sm mb-1">{configOptions?.periodEndLabel || hireConfigPeriodInfo?.endLabel || "End"}:</label>
            <select
              value={period.endMonth}
              onChange={handleEndMonthChange}
              className="w-full p-2 border rounded"
            >
              {endMonthOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Employment Type - Only show if not hidden */}
          {!hideEmploymentType && (
            <div className="mb-3">
              <label className="block text-sm mb-1">{configOptions?.employmentTypeLabel || hireConfigPeriodInfo?.employmentTypeLabel || "Employment Type"}:</label>
              <div className="flex flex-col gap-2">
                {employmentTypes.map((type) => (
                  <label key={type.value} className="flex items-center">
                    <input
                      type="radio"
                      name={`employmentType-${period.id}`}
                      value={type.value}
                      checked={period.employmentType === type.value}
                      onChange={() => handleEmploymentChange(type.value)}
                      className="mr-2"
                    />
                    <span>{type.label}</span>
                    {type.value === 'contractor' && period.employmentType === 'contractor' && (
                      <span className="text-orange-500 ml-1 text-xs">{configOptions?.contractorCostNote || "(+50% cost, no equity)"}</span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end mt-2">
        <button
          onClick={removePeriod}
          className="text-red-500 text-sm hover:text-red-700"
          disabled={isFirstPeriod && isLastPeriod}
          title={isFirstPeriod && isLastPeriod ? (hireConfigPeriodInfo?.cannotRemoveOnlyPeriodTitle || "Cannot remove the only period") : ""}
        >
          {configOptions?.removeLabel || hireConfigPeriodInfo?.removeLabel || "Remove Period"}
        </button>
      </div>
    </div>
  );
};

export default HireConfigPeriod;